import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { CompanyNameType } from 'src/app/models/company.model';
import { DeviceDto } from 'src/app/models/device.model';
import {
  LicenseBatchDto,
  LicenseBatchRequestDto,
  LicensedProductDto,
  ScopeListDto,
} from 'src/app/models/license.model';
import { DeviceService } from 'src/app/services/device.service';
import { DeviceStore } from 'src/app/services/device.store';
import { LicenseBatchService } from 'src/app/services/license-batch.service';
import { LicensedProductService } from 'src/app/services/licensed-product.service';

@Component({
  selector: 'app-license-batch-form',
  templateUrl: './license-batch-form.component.html',
  styleUrls: ['./license-batch-form.component.scss'],
  standalone: false,
})
export class LicenseBatchFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly licenseBatchService = inject(LicenseBatchService);
  private readonly licensedProductService = inject(LicensedProductService);
  private readonly deviceService = inject(DeviceService);
  private readonly deviceStore = inject(DeviceStore);
  private readonly destroyRef = inject(DestroyRef);
  readonly devicesOnlyMode = this.route.snapshot.data['devicesOnly'] === true;

  form: FormGroup = this.formBuilder.nonNullable.group({
    name: this.formBuilder.control('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/\S+/),
        Validators.maxLength(160),
      ],
    }),
    description: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
    companyId: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.maxLength(120)],
    }),
    licensedProductId: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    scopeId: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    licenseBatchSize: this.formBuilder.control(1, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1)],
    }),
    optionalValidAfter: this.formBuilder.control<Date | null>(null),
    optionalValidBefore: this.formBuilder.control<Date | null>(null),
    pendingLicensedDeviceId: this.formBuilder.control('', {
      nonNullable: true,
    }),
    licensedDeviceIds: this.formBuilder.control<string[]>([], {
      nonNullable: true,
    }),
    // An empty value means the batch licenses devices only when they are assigned by hand.
    autoAssignDeviceWithHardwareComponent: this.formBuilder.control('', {
      nonNullable: true,
    }),
  });

  products: LicensedProductDto[] = [];
  scopes: LicenseBatchDto['scope'][] = [];
  autoAssignDeviceTypes: string[] = [];
  autoAssignPreviewDevices: Array<{ id: string; label: string }> = [];
  autoAssignPreviewUnlicensedCount = 0;
  autoAssignPreviewLoading = false;
  autoAssignPreviewFailed = false;
  availableDevices: DeviceDto[] = [];
  companies: CompanyNameType[] = this.normalizeCompaniesData(
    this.route.snapshot.data['companies'],
  );
  selectedProduct: LicensedProductDto | null = null;
  selectedScope: LicenseBatchDto['scope'] | null = null;
  selectedCompany: CompanyNameType | null = null;
  loadedBatch: LicenseBatchDto | null = null;
  editingBatchId: string | null = null;
  loadingDevices = false;
  deviceLoadError = '';
  loading = false;
  saving = false;
  showResponseBanner = false;
  successfulSave = false;
  successMessage = 'License batch saved.';
  errorMessage = 'Failed to save license batch.';
  dateRangeError = '';
  private latestDeviceLookupCompanyId = '';
  private initialLicensedDeviceIds: string[] = [];
  private readonly deviceStoreLoading$ = toObservable(this.deviceStore.loading);
  private readonly deviceStoreDevices$ = toObservable(this.deviceStore.devices);
  private readonly deviceStoreError$ = toObservable(this.deviceStore.error);

  get isEditMode(): boolean {
    return this.editingBatchId !== null;
  }

  get licensedProductIdControl() {
    return this.form.controls['licensedProductId'];
  }

  get companyIdControl() {
    return this.form.controls['companyId'];
  }

  get scopeIdControl() {
    return this.form.controls['scopeId'];
  }

  get nameControl() {
    return this.form.controls['name'];
  }

  get descriptionControl() {
    return this.form.controls['description'];
  }

  get licenseBatchSizeControl() {
    return this.form.controls['licenseBatchSize'];
  }

  get licensedDeviceIdsControl() {
    return this.form.controls['licensedDeviceIds'];
  }

  get autoAssignDeviceWithHardwareComponentControl() {
    return this.form.controls['autoAssignDeviceWithHardwareComponent'];
  }

  get pendingLicensedDeviceIdControl() {
    return this.form.controls['pendingLicensedDeviceId'];
  }

  get hasCompanySelection(): boolean {
    if (this.devicesOnlyMode) {
      return true;
    }
    return !!this.form.controls['companyId'].value.trim();
  }

  get selectableDevices(): DeviceDto[] {
    const selectedIds = new Set(
      this.licensedDeviceIdsControl.value.map((id: string) => id.trim()),
    );
    return this.availableDevices.filter((device) => {
      const deviceId = device.deviceId?.trim();
      return !!deviceId && !selectedIds.has(deviceId);
    });
  }

  get selectedLicensedDevices(): Array<{ id: string; label: string }> {
    const devicesById = new Map<string, DeviceDto>();
    this.availableDevices.forEach((device) => {
      const deviceId = device.deviceId?.trim();
      if (deviceId) {
        devicesById.set(deviceId, device);
      }
    });

    return this.licensedDeviceIdsControl.value
      .map((deviceId: string) => deviceId.trim())
      .filter((deviceId: string) => !!deviceId)
      .map((deviceId: string) => {
        const device = devicesById.get(deviceId);
        return {
          id: deviceId,
          label: device ? this.formatDeviceLabel(device) : deviceId,
        };
      });
  }

  /** The devices this batch already licenses, as loaded; ordered by device id like the preview. */
  get alreadyLicensedDevices(): Array<{ id: string; label: string }> {
    const devicesById = new Map<string, DeviceDto>();
    (this.loadedBatch?.licensedDevices ?? []).forEach((device) => {
      const deviceId = device.deviceId?.trim();
      if (deviceId) {
        devicesById.set(deviceId, device);
      }
    });

    return [...this.initialLicensedDeviceIds].sort().map((deviceId) => {
      const device = devicesById.get(deviceId);
      return {
        id: deviceId,
        label: device ? this.formatDeviceLabel(device) : deviceId,
      };
    });
  }

  /** Auto-assignment licenses devices, so a user-targeted product cannot use it. */
  get supportsAutoAssignment(): boolean {
    return this.selectedProduct?.target !== 'USER';
  }

  get canAddSelectedDevice(): boolean {
    const pendingDeviceId = this.pendingLicensedDeviceIdControl.value.trim();
    if (!pendingDeviceId) {
      return false;
    }
    return this.selectableDevices.some(
      (device) => device.deviceId?.trim() === pendingDeviceId,
    );
  }

  get companyPreSelectedValue(): CompanyNameType | string | null {
    if (this.selectedCompany) {
      return this.selectedCompany;
    }

    const companyId = this.form.controls['companyId'].value.trim();
    return companyId || null;
  }

  get productPreSelectedValue(): LicensedProductDto | string | null {
    if (this.selectedProduct) {
      return this.selectedProduct;
    }

    const productId = this.form.controls['licensedProductId'].value.trim();
    return productId || null;
  }

  get scopePreSelectedValue(): LicenseBatchDto['scope'] | string | null {
    if (this.selectedScope) {
      return this.selectedScope;
    }

    const scopeId = this.form.controls['scopeId'].value.trim();
    return scopeId || null;
  }

  get formTitle(): string {
    if (this.devicesOnlyMode) {
      return 'Manage License Batch Devices';
    }
    return this.isEditMode ? 'Edit License Batch' : 'Create License Batch';
  }

  get submitButtonLabel(): string {
    if (this.devicesOnlyMode) {
      return 'Update Device Links';
    }
    return this.isEditMode ? 'Update Batch' : 'Create Batch';
  }

  ngOnInit(): void {
    const batchId = this.route.snapshot.paramMap.get('id');
    if (!batchId) {
      this.companyIdControl.addValidators([Validators.required]);
      this.companyIdControl.updateValueAndValidity({ emitEvent: false });
    }

    if (!this.devicesOnlyMode) {
      this.loadProducts();
      this.loadScopes();
      this.loadAutoAssignDeviceTypes();
      this.watchAutoAssignPreview();
    }
    if (!batchId) {
      return;
    }
    this.editingBatchId = batchId;
    this.loadBatch(batchId);
  }

  submit(): void {
    if (this.devicesOnlyMode) {
      this.submitDeviceLinkChanges();
      return;
    }

    if (!this.devicesOnlyMode && this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.toRequestPayload();
    if (!payload) {
      return;
    }

    this.saving = true;
    this.showResponseBanner = false;

    if (!this.isEditMode) {
      this.licenseBatchService.create(payload).subscribe({
        next: (batch) => {
          this.handleSaveSuccess('License batch created.', batch.id);
        },
        error: (error: unknown) => {
          this.handleSaveError(error, 'Failed to create license batch.');
        },
      });
      return;
    }

    this.licenseBatchService.update(this.editingBatchId!, payload).subscribe({
      next: (batch) => {
        this.handleSaveSuccess(
          'License batch updated.',
          batch.id ?? this.editingBatchId ?? undefined,
        );
      },
      error: (error: unknown) => {
        this.handleSaveError(error, 'Failed to update license batch.');
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      this.devicesOnlyMode ? '/licenses/batches' : '/admin/licenses/batches',
    ]);
  }

  formatProductLabel(product: LicensedProductDto): string {
    const name = product.name?.trim() || 'Unnamed Product';
    if (!product.id) {
      return name;
    }
    return `${name} (${product.id})`;
  }

  onProductSelected(selected: LicensedProductDto | string): void {
    let product: LicensedProductDto | undefined;
    if (typeof selected === 'string') {
      product = this.findProductByNameOrId(selected);
    } else {
      product = selected;
    }

    this.selectedProduct = product ?? null;
    this.form.controls['licensedProductId'].setValue(product?.id?.trim() ?? '');
    if (!this.supportsAutoAssignment) {
      this.autoAssignDeviceWithHardwareComponentControl.setValue('');
    }
  }

  productFormatter = (
    item: LicensedProductDto | null,
    value: string,
  ): string => {
    if (!item) {
      return value;
    }
    return this.formatProductLabel(item);
  };

  onCompanySelected(selected: CompanyNameType | string): void {
    let company: CompanyNameType | undefined;
    if (typeof selected === 'string') {
      company = this.findCompanyByNameOrId(selected);
    } else {
      company = selected;
    }

    this.selectedCompany = company ?? null;
    const selectedCompanyId = this.selectedCompany?.id ?? '';
    this.form.controls['companyId'].setValue(selectedCompanyId);
  }

  companyFormatter = (item: CompanyNameType | null, value: string): string => {
    if (!item) {
      return value;
    }
    return `${item.name} (${item.id})`;
  };

  formatScopeLabel(scope: LicenseBatchDto['scope']): string {
    const scopeName = scope.name?.trim() || 'Unnamed Scope';
    if (!scope.id) {
      return scopeName;
    }
    return `${scopeName} (${scope.id})`;
  }

  onScopeSelected(selected: LicenseBatchDto['scope'] | string): void {
    let scope: LicenseBatchDto['scope'] | undefined;
    if (typeof selected === 'string') {
      scope = this.findScopeByNameOrId(selected);
    } else {
      scope = selected;
    }

    this.selectedScope = scope ?? null;
    this.scopeIdControl.setValue(scope?.id?.trim() ?? '');
  }

  scopeFormatter = (
    item: LicenseBatchDto['scope'] | null,
    value: string,
  ): string => {
    if (!item) {
      return value;
    }
    return this.formatScopeLabel(item);
  };

  addSelectedDevice(): void {
    if (!this.hasCompanySelection || this.loadingDevices) {
      return;
    }

    const pendingDeviceId = this.pendingLicensedDeviceIdControl.value.trim();
    if (!pendingDeviceId) {
      return;
    }

    const selectedIds = this.licensedDeviceIdsControl.value
      .map((id: string) => id.trim())
      .filter((id: string) => !!id);
    if (selectedIds.includes(pendingDeviceId)) {
      this.pendingLicensedDeviceIdControl.setValue('');
      return;
    }

    this.licensedDeviceIdsControl.setValue([...selectedIds, pendingDeviceId]);
    this.pendingLicensedDeviceIdControl.setValue('');
  }

  removeLicensedDevice(deviceId: string): void {
    const normalizedDeviceId = deviceId?.trim();
    if (!normalizedDeviceId) {
      return;
    }

    const nextIds = this.licensedDeviceIdsControl.value.filter(
      (id: string) => id.trim() !== normalizedDeviceId,
    );
    this.licensedDeviceIdsControl.setValue(nextIds);
  }

  private submitDeviceLinkChanges(): void {
    if (!this.isEditMode || !this.editingBatchId) {
      this.handleSaveError(null, 'Unable to update device links.');
      return;
    }

    const currentDeviceIds = this.normalizeDeviceIds(
      this.licensedDeviceIdsControl.value,
    );
    const currentSet = new Set(currentDeviceIds);
    const initialSet = new Set(this.initialLicensedDeviceIds);

    const deviceIdsToAssign = currentDeviceIds.filter(
      (deviceId) => !initialSet.has(deviceId),
    );
    const deviceIdsToRemove = this.initialLicensedDeviceIds.filter(
      (deviceId) => !currentSet.has(deviceId),
    );

    const operations = [
      ...deviceIdsToAssign.map((deviceId) =>
        this.licenseBatchService.assignDeviceToBatch(
          this.editingBatchId!,
          deviceId,
        ),
      ),
      ...deviceIdsToRemove.map((deviceId) =>
        this.licenseBatchService.removeDeviceFromBatch(
          this.editingBatchId!,
          deviceId,
        ),
      ),
    ];

    this.saving = true;
    this.showResponseBanner = false;

    if (operations.length === 0) {
      this.handleSaveSuccess(
        'No device link changes.',
        this.editingBatchId ?? undefined,
      );
      return;
    }

    forkJoin(operations).subscribe({
      next: () => {
        this.handleSaveSuccess(
          'Device links updated.',
          this.editingBatchId ?? undefined,
        );
      },
      error: (error: unknown) => {
        this.handleSaveError(error, 'Failed to update device links.');
      },
    });
  }

  private toRequestPayload(): LicenseBatchRequestDto | null {
    this.dateRangeError = '';
    const companyId = this.form.controls['companyId'].value.trim() || undefined;
    const scopeId = this.scopeIdControl.value.trim();
    const resolvedScope =
      this.selectedScope ?? this.findScopeByNameOrId(scopeId) ?? null;

    const optionalValidAfter = this.toIsoString(
      this.form.controls['optionalValidAfter'].value,
    );
    const optionalValidBefore = this.toIsoString(
      this.form.controls['optionalValidBefore'].value,
    );

    if (
      optionalValidAfter &&
      optionalValidBefore &&
      new Date(optionalValidBefore).getTime() <=
        new Date(optionalValidAfter).getTime()
    ) {
      this.dateRangeError = '`Valid To` must be after `Valid From`.';
      this.showResponseBanner = true;
      this.successfulSave = false;
      this.errorMessage = this.dateRangeError;
      return null;
    }

    if (!resolvedScope) {
      this.scopeIdControl.setErrors({ required: true });
      this.scopeIdControl.markAsTouched();
      return null;
    }

    return {
      name: this.form.controls['name'].value.trim(),
      description: this.form.controls['description'].value.trim() || undefined,
      companyId,
      scope: {
        id: resolvedScope.id.trim(),
        name: resolvedScope.name.trim(),
      },
      licensedProductId:
        this.form.controls['licensedProductId'].value.trim() || undefined,
      licenseBatchSize: Number(this.form.controls['licenseBatchSize'].value),
      optionalValidAfter,
      optionalValidBefore,
      autoAssignDeviceWithHardwareComponent:
        this.autoAssignDeviceWithHardwareComponentControl.value.trim() ||
        undefined,
    };
  }

  private loadProducts(): void {
    if (this.devicesOnlyMode) {
      this.products = [];
      this.selectedProduct = null;
      return;
    }

    this.licensedProductService.getAll().subscribe({
      next: (result) => {
        this.products = result.list ?? [];
        const selectedProductId =
          this.form.controls['licensedProductId'].value.trim();
        this.selectedProduct =
          this.findProductById(selectedProductId) ?? this.selectedProduct;
      },
      error: () => {
        this.products = [];
        this.selectedProduct = null;
      },
    });
  }

  private loadScopes(): void {
    if (this.devicesOnlyMode) {
      this.scopes = [];
      this.selectedScope = null;
      return;
    }

    this.licenseBatchService.getScopes().subscribe({
      next: (result: ScopeListDto) => {
        this.scopes = (result.scopes ?? []).filter(
          (scope): scope is LicenseBatchDto['scope'] =>
            !!scope?.id?.trim() && !!scope?.name?.trim(),
        );
        const selectedScopeId = this.scopeIdControl.value.trim();
        this.selectedScope =
          this.findScopeById(selectedScopeId) ?? this.selectedScope;
      },
      error: () => {
        this.scopes = [];
        this.selectedScope = null;
      },
    });
  }

  private loadAutoAssignDeviceTypes(): void {
    this.licenseBatchService.getAutoAssignDeviceTypes().subscribe({
      next: (result) => {
        this.autoAssignDeviceTypes = (result.deviceTypes ?? [])
          .map((deviceType) => deviceType?.trim())
          .filter((deviceType): deviceType is string => !!deviceType);
      },
      error: () => {
        this.autoAssignDeviceTypes = [];
      },
    });
  }

  /**
   * Asks the backend, without saving, which devices the batch would license automatically, so the
   * form can show them before the user commits. Only the fields auto-assignment depends on are
   * watched; anything else leaves the preview as it stands.
   */
  private watchAutoAssignPreview(): void {
    // The whole form, not just the device type: the payload is only previewable once every other
    // required field is filled in, and completing one of those is what makes the first preview valid.
    this.form.valueChanges
      .pipe(
        debounceTime(400),
        map(() => this.toAutoAssignPreviewPayload()),
        distinctUntilChanged(
          (previous, next) =>
            this.toAutoAssignPreviewKey(previous) ===
            this.toAutoAssignPreviewKey(next),
        ),
        switchMap((payload) => {
          if (!payload) {
            this.autoAssignPreviewLoading = false;
            return of({ preview: null, failed: false });
          }
          this.autoAssignPreviewLoading = true;
          const preview$ = this.isEditMode
            ? this.licenseBatchService.update(this.editingBatchId!, payload, {
                dryRun: true,
                projection: 'FULL',
              })
            : this.licenseBatchService.create(payload, {
                dryRun: true,
                projection: 'FULL',
              });
          // A preview must never block the form, but a failed one must not read as "nothing to do":
          // it is reported as unavailable instead.
          return preview$.pipe(
            map((preview) => ({ preview, failed: false })),
            catchError(() => of({ preview: null, failed: true })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ preview, failed }) => {
        this.autoAssignPreviewLoading = false;
        this.autoAssignPreviewFailed = failed;
        this.autoAssignPreviewDevices = preview
          ? this.toNewlyLicensedDevices(preview)
          : [];
        this.autoAssignPreviewUnlicensedCount =
          preview?.unlicensedAutoAssignableDeviceCount ?? 0;
      });
  }

  /** The payload to preview with, or null while the form cannot produce a valid one. */
  private toAutoAssignPreviewPayload(): LicenseBatchRequestDto | null {
    if (
      !this.autoAssignDeviceWithHardwareComponentControl.value.trim() ||
      this.form.invalid
    ) {
      return null;
    }
    // toRequestPayload reports its validation to the user; a preview must stay silent.
    const banner = {
      dateRangeError: this.dateRangeError,
      showResponseBanner: this.showResponseBanner,
      successfulSave: this.successfulSave,
      errorMessage: this.errorMessage,
    };
    const payload = this.toRequestPayload();
    Object.assign(this, banner);
    return payload;
  }

  /**
   * What a preview actually depends on. Everything else the payload carries - the name, the
   * description, the validity dates - cannot change which devices are licensed, so editing it
   * must not cost another dry run.
   */
  private toAutoAssignPreviewKey(
    payload: LicenseBatchRequestDto | null,
  ): string {
    if (!payload) {
      return '';
    }
    return JSON.stringify([
      payload.companyId,
      payload.licensedProductId,
      payload.licenseBatchSize,
      payload.autoAssignDeviceWithHardwareComponent,
    ]);
  }

  /** The devices of the previewed batch that it does not license yet. */
  private toNewlyLicensedDevices(
    preview: LicenseBatchDto,
  ): Array<{ id: string; label: string }> {
    const alreadyLicensed = new Set(this.initialLicensedDeviceIds);
    const devicesById = new Map<string, DeviceDto>();
    (preview.licensedDevices ?? []).forEach((device) => {
      const deviceId = device.deviceId?.trim();
      if (deviceId) {
        devicesById.set(deviceId, device);
      }
    });

    return this.normalizeDeviceIds(preview.licensedDeviceIds ?? [])
      .filter((deviceId) => !alreadyLicensed.has(deviceId))
      .map((deviceId) => {
        const device = devicesById.get(deviceId);
        return {
          id: deviceId,
          label: device ? this.formatDeviceLabel(device) : deviceId,
        };
      });
  }

  private loadBatch(batchId: string): void {
    this.loading = true;
    this.licenseBatchService.getById(batchId, 'FULL').subscribe({
      next: (batch) => {
        this.patchBatchForm(batch);
        this.loading = false;
      },
      error: (error: unknown) => {
        this.handleSaveError(error, 'Failed to load license batch.');
        this.loading = false;
      },
    });
  }

  private patchBatchForm(batch: LicenseBatchDto): void {
    this.loadedBatch = batch;
    const resolvedProductId =
      batch.licensedProductId ?? batch.product?.id ?? '';
    const resolvedScopeId = batch.scope?.id?.trim() ?? '';
    const resolvedCompanyId = batch.companyId ?? batch.company?.id ?? '';
    const resolvedCompanyName = batch.company?.name?.trim() ?? '';
    const resolvedDescription =
      batch.description?.trim() ?? batch.description?.trim() ?? '';
    const preSelectedDeviceIds = (
      (batch.licensedDeviceIds?.length
        ? batch.licensedDeviceIds
        : (batch.licensedDevices ?? []).map(
            (device) => device.deviceId ?? '',
          )) ?? []
    ).filter((id): id is string => !!id?.trim());
    this.initialLicensedDeviceIds =
      this.normalizeDeviceIds(preSelectedDeviceIds);
    this.selectedProduct = this.findProductById(resolvedProductId) ?? null;
    this.selectedScope =
      this.findScopeById(resolvedScopeId) ??
      (resolvedScopeId && batch.scope?.name?.trim()
        ? { id: resolvedScopeId, name: batch.scope.name.trim() }
        : null);
    this.selectedCompany =
      this.findCompanyById(resolvedCompanyId) ??
      this.findCompanyByName(resolvedCompanyName) ??
      (resolvedCompanyId && resolvedCompanyName
        ? { id: resolvedCompanyId, name: resolvedCompanyName }
        : null);
    this.form.patchValue({
      name: batch.name?.trim() ?? '',
      description: resolvedDescription,
      companyId: this.selectedCompany?.id ?? resolvedCompanyId,
      licensedProductId: resolvedProductId,
      scopeId: this.selectedScope?.id ?? resolvedScopeId,
      licenseBatchSize: batch.licenseBatchSize ?? 1,
      optionalValidAfter: this.toDateObject(batch.optionalValidAfter),
      optionalValidBefore: this.toDateObject(batch.optionalValidBefore),
      autoAssignDeviceWithHardwareComponent:
        batch.autoAssignDeviceWithHardwareComponent?.trim() ?? '',
    });
    // Shown straight from the loaded batch, rather than waiting for the debounced dry run.
    this.autoAssignPreviewUnlicensedCount =
      batch.unlicensedAutoAssignableDeviceCount ?? 0;

    if (!this.devicesOnlyMode) {
      this.companyIdControl.disable({ emitEvent: false });
      return;
    }

    this.form.patchValue({
      licensedDeviceIds: preSelectedDeviceIds,
    });
    this.loadCompanyDevices(
      this.selectedCompany?.id ?? resolvedCompanyId,
      preSelectedDeviceIds,
    );
  }

  formatDeviceLabel(device: DeviceDto): string {
    const deviceId = device.deviceId?.trim();
    const name =
      device.nameAlias?.trim() || device.deviceSummary?.defaultName?.trim();
    if (name && deviceId) {
      return `${name} (${deviceId})`;
    }
    return name || deviceId || 'Unnamed Device';
  }

  private handleSaveSuccess(message: string, batchId?: string): void {
    this.successfulSave = true;
    this.successMessage = message;
    this.showResponseBanner = true;
    this.saving = false;

    const targetRoute = batchId
      ? this.toBatchDetailsRoute(batchId)
      : [
          this.devicesOnlyMode
            ? '/licenses/batches'
            : '/admin/licenses/batches',
        ];

    setTimeout(() => {
      this.router.navigate(targetRoute);
    }, 600);
  }

  private toBatchDetailsRoute(batchId: string): string[] {
    if (this.devicesOnlyMode) {
      return ['/licenses/batches', batchId];
    }
    return ['/admin/licenses/batches', batchId];
  }

  private handleSaveError(error: unknown, fallbackMessage: string): void {
    this.successfulSave = false;
    this.errorMessage = this.resolveErrorMessage(error, fallbackMessage);
    this.showResponseBanner = true;
    this.saving = false;
  }

  private resolveErrorMessage(error: unknown, fallbackMessage: string): string {
    if (!error) {
      return fallbackMessage;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        error?: { message?: string; title?: string };
        message?: string;
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallbackMessage;
  }

  private toIsoString(
    dateInput: Date | string | null | undefined,
  ): string | undefined {
    if (!dateInput) {
      return undefined;
    }

    if (dateInput instanceof Date) {
      if (isNaN(dateInput.getTime())) {
        return undefined;
      }
      return dateInput.toISOString();
    }

    const trimmedValue = dateInput.trim();
    if (!trimmedValue) {
      return undefined;
    }

    const date = new Date(trimmedValue);
    if (isNaN(date.getTime())) {
      return undefined;
    }
    return date.toISOString();
  }

  private toDateObject(dateString?: string): Date | null {
    if (!dateString) {
      return null;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  }

  private findCompanyById(companyId?: string): CompanyNameType | undefined {
    const normalized = companyId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.companies.find(
      (company) => company.id.toLowerCase() === normalized,
    );
  }

  private findProductById(productId?: string): LicensedProductDto | undefined {
    const normalized = productId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.products.find(
      (product) => product.id?.toLowerCase() === normalized,
    );
  }

  private findProductByNameOrId(
    value?: string,
  ): LicensedProductDto | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId =
      this.extractProductIdFromFormattedValue(raw)?.toLowerCase();

    return this.products.find((product) => {
      const productId = product.id?.toLowerCase();
      const productName = product.name?.toLowerCase();
      const formatted = this.formatProductLabel(product).toLowerCase();
      return (
        productId === normalized ||
        productName === normalized ||
        formatted === normalized ||
        (!!parsedId && productId === parsedId)
      );
    });
  }

  private findScopeById(
    scopeId?: string,
  ): LicenseBatchDto['scope'] | undefined {
    const normalized = scopeId?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.scopes.find((scope) => scope.id.toLowerCase() === normalized);
  }

  private findScopeByNameOrId(
    value?: string,
  ): LicenseBatchDto['scope'] | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId = this.extractIdFromFormattedValue(raw)?.toLowerCase();

    return this.scopes.find((scope) => {
      const scopeId = scope.id?.toLowerCase();
      const scopeName = scope.name?.toLowerCase();
      const formatted = this.formatScopeLabel(scope).toLowerCase();
      return (
        scopeId === normalized ||
        scopeName === normalized ||
        formatted === normalized ||
        (!!parsedId && scopeId === parsedId)
      );
    });
  }

  private findCompanyByNameOrId(value?: string): CompanyNameType | undefined {
    const raw = value?.trim();
    const normalized = raw?.toLowerCase();
    if (!raw || !normalized) {
      return undefined;
    }

    const parsedId =
      this.extractCompanyIdFromFormattedValue(raw)?.toLowerCase();

    return this.companies.find(
      (company) =>
        company.id.toLowerCase() === normalized ||
        company.name.toLowerCase() === normalized ||
        `${company.name} (${company.id})`.toLowerCase() === normalized ||
        (!!parsedId && company.id.toLowerCase() === parsedId),
    );
  }

  private findCompanyByName(companyName?: string): CompanyNameType | undefined {
    const normalized = companyName?.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.companies.find(
      (company) => company.name.trim().toLowerCase() === normalized,
    );
  }

  private extractCompanyIdFromFormattedValue(
    value: string,
  ): string | undefined {
    const match = value.match(/\(([^()]+)\)\s*$/);
    return match?.[1]?.trim();
  }

  private extractIdFromFormattedValue(value: string): string | undefined {
    const match = value.match(/\(([^()]+)\)\s*$/);
    return match?.[1]?.trim();
  }

  private extractProductIdFromFormattedValue(
    value: string,
  ): string | undefined {
    return this.extractIdFromFormattedValue(value);
  }

  private normalizeDeviceIds(deviceIds: string[]): string[] {
    return Array.from(
      new Set(
        deviceIds
          .map((deviceId: string) => deviceId.trim())
          .filter((deviceId: string) => !!deviceId),
      ),
    );
  }

  private normalizeCompaniesData(companiesData: unknown): CompanyNameType[] {
    if (Array.isArray(companiesData)) {
      return companiesData;
    }

    if (companiesData && typeof companiesData === 'object') {
      const typedData = companiesData as {
        companies?: CompanyNameType[];
        list?: CompanyNameType[];
      };
      if (Array.isArray(typedData.companies)) {
        return typedData.companies;
      }
      if (Array.isArray(typedData.list)) {
        return typedData.list;
      }
    }

    return [];
  }

  private loadCompanyDevices(
    companyId: string,
    preSelectedDeviceIds: string[],
  ): void {
    const normalizedCompanyId = companyId?.trim().toLowerCase() ?? '';
    this.latestDeviceLookupCompanyId = normalizedCompanyId;
    this.deviceLoadError = '';

    if (!normalizedCompanyId && !this.devicesOnlyMode) {
      this.availableDevices = [];
      this.pendingLicensedDeviceIdControl.setValue('');
      this.pendingLicensedDeviceIdControl.disable({ emitEvent: false });
      this.licensedDeviceIdsControl.setValue([]);
      this.licensedDeviceIdsControl.disable({ emitEvent: false });
      this.loadingDevices = false;
      return;
    }

    this.loadingDevices = true;
    this.pendingLicensedDeviceIdControl.setValue('');
    this.pendingLicensedDeviceIdControl.disable({ emitEvent: false });
    this.licensedDeviceIdsControl.disable({ emitEvent: false });

    if (this.devicesOnlyMode) {
      this.deviceStore.getCompanyDevices('BRIEF');
      combineLatest([
        this.deviceStoreLoading$,
        this.deviceStoreDevices$,
        this.deviceStoreError$,
      ])
        .pipe(
          filter(([loading]) => !loading),
          take(1),
        )
        .subscribe({
          next: ([, devices, error]) => {
            if (this.latestDeviceLookupCompanyId !== normalizedCompanyId) {
              return;
            }

            if (error) {
              this.availableDevices = [];
              this.pendingLicensedDeviceIdControl.setValue('');
              this.pendingLicensedDeviceIdControl.disable({ emitEvent: false });
              this.licensedDeviceIdsControl.setValue([]);
              this.licensedDeviceIdsControl.disable({ emitEvent: false });
              this.deviceLoadError =
                'Failed to load devices for selected company.';
              this.loadingDevices = false;
              return;
            }

            this.availableDevices = (devices ?? []).filter(
              (device: DeviceDto) => !!device.deviceId?.trim(),
            );

            const availableDeviceIds = this.availableDevices
              .map((device: DeviceDto) => device.deviceId?.trim() ?? '')
              .filter((deviceId: string) => !!deviceId);
            const allowedIdSet = new Set(availableDeviceIds);
            const selectedIds = Array.from(
              new Set(
                preSelectedDeviceIds
                  .map((deviceId: string) => deviceId.trim())
                  .filter((deviceId: string) => allowedIdSet.has(deviceId)),
              ),
            );

            this.licensedDeviceIdsControl.setValue(selectedIds);
            this.pendingLicensedDeviceIdControl.setValue('');
            this.pendingLicensedDeviceIdControl.enable({ emitEvent: false });
            this.licensedDeviceIdsControl.enable({ emitEvent: false });
            this.loadingDevices = false;
          },
        });
      return;
    }

    this.deviceService.getDevicesByCompanyId(companyId, 'BRIEF').subscribe({
      next: (result) => {
        if (this.latestDeviceLookupCompanyId !== normalizedCompanyId) {
          return;
        }

        this.availableDevices = (result.devices ?? []).filter(
          (device: DeviceDto) => !!device.deviceId?.trim(),
        );

        const availableDeviceIds = this.availableDevices
          .map((device: DeviceDto) => device.deviceId?.trim() ?? '')
          .filter((deviceId: string) => !!deviceId);
        const allowedIdSet = new Set(availableDeviceIds);
        const selectedIds = Array.from(
          new Set(
            preSelectedDeviceIds
              .map((deviceId: string) => deviceId.trim())
              .filter((deviceId: string) => allowedIdSet.has(deviceId)),
          ),
        );

        this.licensedDeviceIdsControl.setValue(selectedIds);
        this.pendingLicensedDeviceIdControl.setValue('');
        this.pendingLicensedDeviceIdControl.enable({ emitEvent: false });
        this.licensedDeviceIdsControl.enable({ emitEvent: false });
        this.loadingDevices = false;
      },
      error: () => {
        if (this.latestDeviceLookupCompanyId !== normalizedCompanyId) {
          return;
        }
        this.availableDevices = [];
        this.pendingLicensedDeviceIdControl.setValue('');
        this.pendingLicensedDeviceIdControl.disable({ emitEvent: false });
        this.licensedDeviceIdsControl.setValue([]);
        this.licensedDeviceIdsControl.disable({ emitEvent: false });
        this.deviceLoadError = 'Failed to load devices for selected company.';
        this.loadingDevices = false;
      },
    });
  }
}
